import { useState, useCallback } from 'react';
import type {
  EditParams,
  CommentMention,
  CommentAttachment
} from '../../containers/CommentList/CommentList.types';

/**
 * Unified hook for managing edit state - supports both single and multiple edits
 * Consolidates functionality from useEditState and useCommentEdit
 * Follows DRY principle by eliminating duplicate edit management logic
 */
export const useEditManager = (
  // Global edit handler (for multi-edit scenarios)
  handleEdit?: (params: EditParams) => void
) => {
  // Multi-edit state management
  const [editingItems, setEditingItems] = useState<
    Map<
      string,
      {
        originalText: string;
        currentText: string;
        originalMentions?: CommentMention[];
        originalAttachments?: (CommentAttachment | File)[];
        isControlled: boolean;
        parentCommentId?: string;
        onEditStart?: () => void;
        onEditComplete?: (text: string, wasChanged: boolean) => void;
        onEditCancel?: () => void;
      }
    >
  >(new Map());

  // Multi-edit API (replaces useCommentEdit functionality)
  const startEdit = useCallback(
    (
      id: string,
      originalText: string = '',
      isControlled: boolean = false,
      parentCommentId?: string,
      onEditStart?: () => void,
      onEditComplete?: (text: string, wasChanged: boolean) => void,
      onEditCancel?: () => void,
      originalMentions?: CommentMention[],
      originalAttachments?: (CommentAttachment | File)[]
    ) => {
      setEditingItems(prev =>
        new Map(prev).set(id, {
          originalText,
          currentText: originalText,
          originalMentions,
          originalAttachments,
          isControlled,
          parentCommentId,
          onEditStart,
          onEditComplete,
          onEditCancel
        })
      );

      // Call lifecycle callback
      if (onEditStart) {
        onEditStart();
      }
    },
    []
  );

  const cancelEdit = useCallback(
    (id: string) => {
      const editItem = editingItems.get(id);
      if (editItem) {
        // Call lifecycle callback
        if (editItem.onEditCancel) {
          editItem.onEditCancel();
        }

        setEditingItems(prev => {
          const next = new Map(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [editingItems]
  );

  const submitEdit = useCallback(
    async (
      id: string,
      newText: string,
      mentions?: CommentMention[],
      attachments?: (CommentAttachment | File)[]
    ) => {
      const editItem = editingItems.get(id);
      if (!editItem) return;

      // Check if text has changed
      const textChanged = newText.trim() !== editItem.originalText.trim();

      // Check if mentions have changed
      const originalMentions = editItem.originalMentions || [];
      const newMentions = mentions || [];
      const mentionsChanged =
        originalMentions.length !== newMentions.length ||
        originalMentions.some((om, index) => {
          const nm = newMentions[index];
          return !nm || om.uid !== nm.uid;
        });

      const originalAttachments = editItem.originalAttachments ?? [];
      const newAttachments = attachments ?? [];
      const attachmentsChanged =
        originalAttachments.length !== newAttachments.length;

      const wasChanged = textChanged || mentionsChanged || attachmentsChanged;

      // Call lifecycle callback
      if (editItem.onEditComplete) {
        editItem.onEditComplete(newText, wasChanged);
      }

      // Call global edit handler if provided
      if (handleEdit) {
        const params: EditParams = {
          id,
          text: newText,
          commentMentions: mentions,
          parentCommentId: editItem.parentCommentId || id,
          wasChanged,
          ...(attachments !== undefined && { attachments })
        };

        handleEdit(params);
      }

      // Remove from editing state
      setEditingItems(prev => {
        const next = new Map(prev);
        next.delete(id);
        return next;
      });
    },
    [editingItems, handleEdit]
  );

  const updateEditText = useCallback((id: string, newText: string) => {
    setEditingItems(prev => {
      const editItem = prev.get(id);
      if (!editItem) return prev;

      const next = new Map(prev);
      next.set(id, { ...editItem, currentText: newText });
      return next;
    });
  }, []);

  const isEditing = useCallback(
    (id: string) => editingItems.has(id),
    [editingItems]
  );

  const getEditText = useCallback(
    (id: string) => {
      return editingItems.get(id)?.currentText || '';
    },
    [editingItems]
  );

  return {
    // Multi-edit API (useCommentEdit replacement)
    editingItems: Array.from(editingItems.keys()),
    startEdit,
    cancelEdit,
    submitEdit,
    updateEditText,
    isEditing,
    getEditText
  };
};

/**
 * Single-edit convenience hook that uses internal state management
 * Provides the same API as the old useEditState for backward compatibility
 */
export { useSingleEdit } from './useSingleEdit';
